import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

export default function EmailContinuitySection() {
  const { activeWorkspaceId } = useAuth();
  const [fallback, setFallback] = useState("");
  const [members, setMembers] = useState([]);
  const [resolved, setResolved] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    if (!activeWorkspaceId) return;
    try {
      const [{ data: s }, { data: m }] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/settings/email-continuity`),
        api.get(`/workspaces/${activeWorkspaceId}/members`),
      ]);
      setFallback(s.email_fallback_owner_id || "");
      setResolved(s.resolved_owner || null);
      setMembers((m || []).filter((x) => x.status === "active"));
    } catch { /* ok */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/workspaces/${activeWorkspaceId}/settings/email-continuity`, {
        email_fallback_owner_id: fallback || null,
      });
      toast.success("Fallback owner saved");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-border p-6 space-y-3" data-testid="settings-email-continuity">
      <div className="label-eyebrow">Business continuity — email fallback</div>
      <p className="text-sm text-muted-foreground">
        When an employee is deactivated, all Gmail threads they own get automatically reassigned to this user.
        The new owner sees them in Inbox under the <strong>Inherited</strong> filter and can take over the conversation using their own connected Gmail.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <select value={fallback} onChange={(e) => setFallback(e.target.value)}
                className="h-10 border border-border bg-background px-2 text-sm min-w-[280px]" data-testid="settings-fallback-select">
          <option value="">— Use workspace owner —</option>
          {members.map((m) => (
            <option key={m.user_id} value={m.user_id}>{m.name || m.email} ({m.role})</option>
          ))}
        </select>
        <Button className="rounded-none" onClick={save} disabled={busy} data-testid="settings-fallback-save">
          {busy ? "Saving…" : "Save"}
        </Button>
        {resolved && (
          <span className="text-xs text-muted-foreground">
            Current: <strong>{resolved.name || resolved.email}</strong>
          </span>
        )}
      </div>
    </div>
  );
}
